import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'

import { Button } from '@src/quince-components/src'
import Modal from '@src/quince-components/src/atoms/modal/Modal'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import styles from './SignInCodeModal.module.scss'

interface SignInCodeModalProps {
  open: boolean
  onClose: () => void
  email: string
  onSubmit: (otp: string) => Promise<void>
  onResend: () => void
  error?: string | null
  loading?: boolean
  resendLoading?: boolean
}

const SignInCodeModal = ({
  open,
  onClose,
  email,
  onSubmit,
  onResend,
  error,
  loading,
  resendLoading,
}: SignInCodeModalProps) => {
  const [otpCodeText, setOtpCodeText] = useState('')

  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    if (!open) {
      setOtpCodeText('')
    } else {
      trackEvent(TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_MODAL_OPEN, { email })
    }
  }, [open, email])

  const handleSubmitOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      await onSubmit(otpCodeText)
    } catch (error) {
      setOtpCodeText('')
    }
  }

  const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setOtpCodeText(value)
  }

  return (
    <Modal open={open} onClose={onClose} className={styles['signin-code-modal']}>
      <div data-testid="signin-code-modal" className={styles['signin-code-modal__content']}>
        <h2 className={styles['signin-code-modal__header']}>{t('signin_verification')}</h2>

        <p className={styles['signin-code-modal__description']}>
          {t('signin_verification_code_prompt')}
          <span className={styles['signin-code-modal__email']}>{` ${email}`}</span>
        </p>

        <form
          onSubmit={e => {
            void handleSubmitOtp(e)
          }}
          className={styles['signin-code-modal__form']}
        >
          <div className={styles['signin-code-modal__input-wrapper']}>
            <input
              type="text"
              className={classNames(styles['signin-code-modal__input'], {
                [styles['signin-code-modal__input--error'] as string]: !!error,
              })}
              placeholder={t('enter_code')}
              value={otpCodeText}
              onChange={handleInputValueChange}
              autoFocus
              maxLength={6}
              inputMode="numeric"
              pattern="[0-9]*"
              data-testid="signin-code-modal-input"
            />
          </div>

          {error && <div className={styles['signin-code-modal__error']}>{error}</div>}

          <Button
            onClick={e => {
              void handleSubmitOtp(e)
            }}
            id="signin-code-modal-submit-button"
            data-testid="signin-code-modal-submit-button"
            className={styles['signin-code-modal__button']}
            disabled={!otpCodeText || loading}
            showLoader={loading}
          >
            {tCommon('submit')}
          </Button>
        </form>

        <div className={styles['signin-code-modal__resend-container']}>
          <span>{t('didnt_get_the_code')}</span>
          <button
            id="signin-code-modal-resend-button"
            data-testid="signin-code-modal-resend-button"
            className={styles['signin-code-modal__resend-button']}
            onClick={onResend}
            disabled={resendLoading}
          >
            {resendLoading ? t('sending') : t('resend')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SignInCodeModal
